export type TemplateSavePlan =
  | { kind: 'create' }
  | { kind: 'update'; isPrivate: boolean | null | undefined }
  | { kind: 'use-existing' };

export type PersistingTemplateSavePlan = Exclude<
  TemplateSavePlan,
  { kind: 'use-existing' }
>;

type TemplateEditorSavePlanInput = {
  templateId?: string | null;
  isEditable?: boolean;
  isPrivate?: boolean | null;
};

export const getUpdateTemplateSavePlan = (
  isPrivate: boolean | null | undefined
): PersistingTemplateSavePlan => ({
  kind: 'update',
  isPrivate,
});

export const getTemplateFormSavePlan = ({
  existingTemplateId,
  isPrivate,
}: {
  existingTemplateId?: string | null;
  isPrivate?: boolean | null;
}): PersistingTemplateSavePlan =>
  existingTemplateId ? getUpdateTemplateSavePlan(isPrivate) : { kind: 'create' };

export function getTemplateEditorSavePlan(
  input: TemplateEditorSavePlanInput & { action: 'save' }
): PersistingTemplateSavePlan;
export function getTemplateEditorSavePlan(
  input: TemplateEditorSavePlanInput & { action: 'save-and-use' }
): TemplateSavePlan;
export function getTemplateEditorSavePlan(
  input: TemplateEditorSavePlanInput & { action: 'save' | 'save-and-use' }
): TemplateSavePlan;
export function getTemplateEditorSavePlan(
  input: TemplateEditorSavePlanInput & { action: 'save' | 'save-and-use' }
): TemplateSavePlan {
  if (!input.templateId) {
    return { kind: 'create' };
  }

  if (input.isEditable) {
    return getUpdateTemplateSavePlan(input.isPrivate);
  }

  return input.action === 'save' ? { kind: 'create' } : { kind: 'use-existing' };
}

/**
 * Source of truth for whether a save plan writes a new bespoke template.
 *
 * `useMutateUpdateTemplate` creates a copy unless `is_private` is explicitly
 * true, so an update plan with false or missing privacy must be gated as a
 * create too.
 */
export const willTemplateSaveCreate = (savePlan: TemplateSavePlan): boolean =>
  savePlan.kind === 'create' ||
  (savePlan.kind === 'update' && savePlan.isPrivate !== true);
